import {t} from "@lingui/core/macro";
import * as React from "react";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {
    useMarkConversion,
    usePublishAnalyticsEvent,
} from "@repo/analytics-learning-time";
import {LoginButton} from "@repo/auth-shared";
import {RequestInfo} from "@repo/shared-core";
import {preserveMarketingParams, redirect, useLocation} from "@repo/urls";

import {CleverIcon} from "./clever-icon.tsx";

interface Props {
    style?: StyleType;
    size?: "small" | "medium" | "large";
    reauth?: boolean;
    isLogin: boolean;
    continueUrl?: string;
    iconOnly?: boolean;
    disabled?: boolean;
}

export function CleverLibraryButton(props: Props): React.ReactNode {
    const {style, size} = props;
    const location = useLocation();
    const markConversion = useMarkConversion();
    const publishEvent = usePublishAnalyticsEvent();

    const handleClick = (e: React.SyntheticEvent): void => {
        const {isLogin, continueUrl, reauth} = props;
        let redirectPath = reauth
            ? "/login/clever/reauth"
            : "/login/clever/redirect";
        let clientID = "e50a9862bc4fdeafda80";
        if (RequestInfo.IS_DEV_SERVER) {
            redirectPath = reauth
                ? "/login/clever/reauth_dev"
                : "/login/clever/redirect_dev";
            clientID = "710adf38547c9dbef1ad";
        }

        const redirectURI = location.origin + redirectPath;
        let cleverSearchParams = `?response_type=code&redirect_uri=${redirectURI}&client_id=${clientID}`;
        if (continueUrl) {
            const continueUrlWithParams = preserveMarketingParams(
                continueUrl,
                new URLSearchParams(location.search),
            );
            cleverSearchParams += `&state=${continueUrlWithParams}`;
        }

        const cleverURL = `https://clever.com/oauth/authorize${cleverSearchParams}`;

        if (isLogin) {
            markConversion({
                id: "login_clever_button_clicked",
            });
        } else {
            publishEvent(
                {
                    eventType: "SignupCleverButtonClicked",
                    eventSchemaVersion: 1,
                },
                "signup_clever_button_clicked",
            );
        }

        redirect(cleverURL);
    };

    return (
        <LoginButton
            style={style}
            size={size}
            title={t`Continue with Clever`}
            iconComponent={CleverIcon}
            disabled={props.disabled}
            onClick={handleClick}
            iconOnly={props.iconOnly}
        />
    );
}
